import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CRow, CCol,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CButton, CFormInput, CSpinner, CAlert, CBadge, CInputGroup, CInputGroupText,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowLeft, cilGift, cilSearch } from '@coreui/icons';
import api from '../../services/api';

const TYPE_LABEL = {
  job_earn:     { label: 'Job Earn',    color: 'success' },
  reg_bonus:    { label: 'Reg Bonus',   color: 'info'    },
  points_spend: { label: 'Redeemed',    color: 'danger'  },
};

function txnLabel(type) {
  return TYPE_LABEL[type] || { label: type, color: 'secondary' };
}

export default function PointsHistoryPage() {
  const navigate = useNavigate();
  const [txns, setTxns]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [search, setSearch]   = useState('');
  const [draft, setDraft]     = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Summary stats (from first load, no search filter)
  const [stats, setStats] = useState(null);

  const load = useCallback(async (p, q) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/finance/points-history', { params: { page: p, search: q } });
      setTxns(data.txns);
      setTotal(data.total);
      setPageSize(data.pageSize);
    } catch {
      setError('Failed to load points history.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load summary stats once (unfiltered totals from dashboard-like query)
  useEffect(() => {
    api.get('/finance/points-history', { params: { page: 1, search: '' } })
      .then(({ data }) => {
        const created = data.txns.reduce((s, t) => t.points > 0 ? s + t.points : s, 0);
        const spent   = data.txns.reduce((s, t) => t.points < 0 ? s + Math.abs(t.points) : s, 0);
        // Use dashboard endpoint for accurate totals
        api.get('/dashboard').then(r => {
          const ps = r.data?.finance?.pointsSummary;
          if (ps) setStats({ totalCreated: ps.totalCreated, totalSpent: ps.totalSpent });
        }).catch(() => {});
      }).catch(() => {});
  }, []);

  useEffect(() => { load(page, search); }, [page, search, load]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(draft);
  }

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0"><CIcon icon={cilGift} className="me-2 text-warning" />FMZ Points History</h5></CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" component={Link} to="/finance/invoices">
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>
        </CCol>
      </CRow>

      {/* Summary cards */}
      {stats && (
        <CRow className="mb-4 g-3">
          <CCol xs={12} sm={4}>
            <CCard className="border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
              <CCardBody className="py-4">
                <div className="text-muted small mb-1">Total Points Issued</div>
                <div className="fw-bold text-success" style={{ fontSize: 28 }}>{stats.totalCreated.toLocaleString()}</div>
                <div className="text-muted small">pts</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol xs={12} sm={4}>
            <CCard className="border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
              <CCardBody className="py-4">
                <div className="text-muted small mb-1">Total Points Redeemed</div>
                <div className="fw-bold text-danger" style={{ fontSize: 28 }}>{stats.totalSpent.toLocaleString()}</div>
                <div className="text-muted small">pts</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol xs={12} sm={4}>
            <CCard className="border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
              <CCardBody className="py-4">
                <div className="text-muted small mb-1">Net Outstanding Points</div>
                <div className="fw-bold text-primary" style={{ fontSize: 28 }}>{(stats.totalCreated - stats.totalSpent).toLocaleString()}</div>
                <div className="text-muted small">pts</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white py-3 px-4">
          <form onSubmit={handleSearch} className="d-flex gap-2" style={{ maxWidth: 400 }}>
            <CInputGroup>
              <CInputGroupText><CIcon icon={cilSearch} /></CInputGroupText>
              <CFormInput
                placeholder="Customer name, UID, job code…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
              />
            </CInputGroup>
            <CButton color="success" type="submit" size="sm">Search</CButton>
            {search && (
              <CButton color="secondary" variant="outline" size="sm" onClick={() => { setDraft(''); setSearch(''); setPage(1); }}>
                Clear
              </CButton>
            )}
          </form>
        </CCardHeader>
        <CCardBody className="p-0">
          {error && <CAlert color="danger" className="m-3">{error}</CAlert>}
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : (
            <>
              <CTable hover responsive className="mb-0" style={{ fontSize: 14 }}>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell>#</CTableHeaderCell>
                    <CTableHeaderCell>Customer</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Note</CTableHeaderCell>
                    <CTableHeaderCell>Job</CTableHeaderCell>
                    <CTableHeaderCell>Invoice</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Points</CTableHeaderCell>
                    <CTableHeaderCell>Date</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {txns.length === 0 ? (
                    <CTableRow>
                      <CTableDataCell colSpan={8} className="text-center text-muted py-4">No transactions found.</CTableDataCell>
                    </CTableRow>
                  ) : txns.map((t) => {
                    const { label, color } = txnLabel(t.type);
                    return (
                      <CTableRow key={t.id}>
                        <CTableDataCell className="text-muted">{t.id}</CTableDataCell>
                        <CTableDataCell>
                          <div className="fw-semibold">{t.customer_name || '—'}</div>
                          <div className="text-muted small">{t.customer_uid}</div>
                        </CTableDataCell>
                        <CTableDataCell><CBadge color={color}>{label}</CBadge></CTableDataCell>
                        <CTableDataCell className="text-muted small">{t.note || '—'}</CTableDataCell>
                        <CTableDataCell>
                          {t.ref_jobcode
                            ? <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${t.ref_jobcode}/edit`)}>{t.ref_jobcode}</span>
                            : '—'}
                        </CTableDataCell>
                        <CTableDataCell>
                          {t.ref_invoice
                            ? <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => navigate(`/finance/invoices/${t.ref_invoice}`)}>#{t.ref_invoice}</span>
                            : '—'}
                        </CTableDataCell>
                        <CTableDataCell className={`text-end fw-bold ${t.points > 0 ? 'text-success' : 'text-danger'}`}>
                          {t.points > 0 ? '+' : ''}{t.points}
                        </CTableDataCell>
                        <CTableDataCell className="text-muted small">
                          {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top small text-muted">
                  <span>Showing {txns.length} of {total} transactions</span>
                  <div className="d-flex gap-2">
                    <CButton size="sm" color="secondary" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</CButton>
                    <span className="align-self-center">Page {page} / {totalPages}</span>
                    <CButton size="sm" color="secondary" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</CButton>
                  </div>
                </div>
              )}
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
